import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Mail,
  Phone,
  MapPin,
  FileText,
  Award,
  GraduationCap,
  Briefcase,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Globe,
  Linkedin,
  Github,
  ExternalLink,
  Trash2,
  Download,
  History,
  Eye,
  UserCheck,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import candidateService from "../../../api/services/candidateService";
import { API_BASE_URL } from "../../../api/config";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import { formatDate } from "../../../utils/stringUtils";
import CustomAlert from "../../../components/common/CustomAlert";

const ViewCandidate = ({ onBack, id: propId }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const { alertState, showSuccess, showError, showWarning, hideAlert } = useCustomAlert();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        setLoading(true);
        setError(null);


        const result = await candidateService.getCandidateById(id);

        // Check if result has the expected structure
        if (result && result.responseCode === 200 && result.data) {
          // Standard API response format - check if data has nested candidate object
          if (result.data.candidate) {
            setCandidate(result.data.candidate);

          } else {
            // Direct candidate data in data property
            setCandidate(result.data);

          }
        } else if (result && result.id && result.candidateId) {
          // Direct data format (no wrapper)
          setCandidate(result);

        } else {
          setError(result?.responseMessage || "Failed to fetch candidate");

        }
      } catch (err) {

        setError("Failed to fetch candidate details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCandidate();
    }
  }, [id]);

  const handleDownloadResume = async () => {
    try {
      setIsDownloading(true);


      // Check if candidate has resume data
      if (!candidate.resume) {
        showWarning("Document not found");
        return;
      }

      const resolvedFullName =
        (candidate?.name && candidate.name.trim()) ||
        [candidate?.firstName, candidate?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        "Candidate";
      const defaultBaseName = `${resolvedFullName.replace(/\s+/g, "_")}_Resume`;

      const parseFilenameFromContentDisposition = (contentDisposition) => {
        if (!contentDisposition) return "";

        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (utf8Match?.[1]) {
          try {
            return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, "");
          } catch (_) {
            return utf8Match[1].replace(/(^"|"$)/g, "");
          }
        }

        const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
        return simpleMatch?.[1]
          ? simpleMatch[1].trim().replace(/(^"|"$)/g, "")
          : "";
      };

      const ensureFilenameHasExtension = (filename, mimeType) => {
        const clean = String(filename || defaultBaseName).trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const type = String(mimeType || "").toLowerCase();
        if (type === "application/pdf") return `${clean}.pdf`;
        if (type === "application/msword") return `${clean}.doc`;
        if (
          type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return `${clean}.docx`;
        if (type === "text/plain") return `${clean}.txt`;
        return `${clean}.pdf`;
      };

      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/recruitment/candidates/${id}/resume`,
        {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          showWarning("Document not found");
          return;
        }
        throw new Error("Failed to download resume");
      }

      const blob = await response.blob();
      const headerFileName = parseFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      );
      const fallbackFileName =
        candidate?.resume?.originalName ||
        candidate?.resume?.filename ||
        defaultBaseName;
      const resolvedFileName = ensureFilenameHasExtension(
        headerFileName || fallbackFileName,
        blob.type || candidate?.resume?.mimetype || candidate?.resume?.mimeType,
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      showSuccess("Resume downloaded successfully");
    } catch (error) {

      showError("Failed to download resume. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/edit_candidate/${id}`, { state: { from: location.state?.from } });
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const result = await candidateService.deleteCandidate(id);
      if (result && result.responseCode === 200) {
        showSuccess("Candidate deleted successfully");
        navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles");
      } else {
        showError(result?.responseMessage || "Failed to delete candidate");
      }
    } catch (error) {

      showError("Failed to delete candidate");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "applied":
        return "bg-blue-50 text-brand border border-blue-100";
      case "shortlisted":
        return "bg-blue-50/30 text-brand border border-blue-100";
      case "interviewed":
        return "bg-blue-50/30 text-brand border border-blue-100";
      case "documentation":
        return "bg-teal-50 text-teal-700 border border-teal-100";
      case "offered":
        return "bg-blue-50/50 text-brand border border-blue-100";
      case "hired":
        return "bg-blue-50/50 text-brand border border-blue-100";
      case "rejected":
        return "bg-red-50 text-red-700 border border-red-100";
      case "backout":
        return "bg-amber-50 text-amber-700 border border-amber-100";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "applied":
        return <Clock className="h-4 w-4" />;
      case "shortlisted":
        return <Star className="h-4 w-4" />;
      case "interviewed":
        return <User className="h-4 w-4" />;
      case "documentation":
        return <FileText className="h-4 w-4" />;
      case "offered":
        return <CheckCircle className="h-4 w-4" />;
      case "hired":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      case "backout":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const KpiMiniCard = ({ icon: Icon, title, value }) => (
    <div className="relative overflow-hidden bg-white rounded-2xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
      <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-brand opacity-5 group-hover:opacity-10 transition-opacity duration-300" />
      <div className="relative z-10 flex items-center gap-3">
        <div className="p-2 rounded-xl bg-blue-50/30 border border-blue-100 shadow-sm">
          <Icon className="h-5 w-5 text-brand" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider truncate">{title}</p>
          <p className="text-sm font-bold text-gray-900 tracking-tight break-words">{value}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Candidate Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            {error || "The candidate you're looking for doesn't exist."}
          </p>
          <Button
            onClick={() => navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles")}
            variant="primary"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const getDisplayValue = (value, fallback = "Not specified") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string" && value.trim() === "") return fallback;
    return value;
  };

  const derivedFullName = [candidate?.firstName, candidate?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const fullName = getDisplayValue(candidate?.name || derivedFullName);
  const jobTitle = getDisplayValue(
    candidate?.jobTitle || candidate?.job?.title,
  );
  const jobDepartment = getDisplayValue(candidate?.department);
  const jobLocation = getDisplayValue(
    candidate?.jobLocation || candidate?.job?.location,
  );
  const jobTypeRaw = candidate?.jobType || candidate?.job?.type;
  const jobType = getDisplayValue(
    jobTypeRaw ? jobTypeRaw.replace(/_/g, " ") : "",
  );
  const jobId = getDisplayValue(
    candidate?.jobId || candidate?.job?.jobId || candidate?.job?.id,
  );
  const candidateId = getDisplayValue(candidate?.candidateId, "N/A");
  const currentCompany = getDisplayValue(candidate?.currentCompany);
  const availability = getDisplayValue(candidate?.availability);
  const education = getDisplayValue(candidate?.education);
  const university = getDisplayValue(candidate?.university);
  const experience =
    candidate?.experience && typeof candidate.experience === "object"
      ? getDisplayValue(candidate.experience.name)
      : getDisplayValue(candidate?.experience);
  const source = getDisplayValue(
    candidate?.source ? candidate.source.replace(/_/g, " ") : "",
  );

  // Creator Information (Fallback for string names vs ObjectIds)
  const creator =
    candidate?.createdByUser ||
    candidate?.creator ||
    (typeof candidate?.createdBy === "object" ? candidate?.createdBy : null);
  const isPublicApplication = !candidate?.createdBy && !creator?.name;
  const hasCreatorRef = Boolean(
    creator?.hasReferenceId ||
    (creator?.id && creator.id !== "N/A" && typeof candidate?.createdBy !== "string"),
  );

  const creatorName = isPublicApplication
    ? "Direct Applicant (Career Page)"
    : getDisplayValue(
      creator?.name ||
      (typeof candidate?.createdBy === "string" ? candidate?.createdBy : ""),
      "N/A",
    );
  const creatorDesignation = hasCreatorRef
    ? getDisplayValue(creator?.designation, "N/A")
    : null;
  const isMongoObjectId = (val) =>
    typeof val === "string" && /^[0-9a-fA-F]{24}$/.test(val.trim());

  const creatorEmployeeId = hasCreatorRef
    ? getDisplayValue(
      creator?.employeeId && !isMongoObjectId(creator.employeeId)
        ? creator.employeeId
        : "",
      null,
    )
    : null;
  const rawCreatedAt = candidate?.createdAt || candidate?.appliedDate;
  const createdAtFormatted = rawCreatedAt
    ? formatDate(rawCreatedAt, { includeTime: true })
    : null;

  // Updater Information
  const updater =
    candidate?.updatedByUser ||
    candidate?.updater ||
    (typeof candidate?.updatedBy === "object" ? candidate?.updatedBy : null);
  const hasUpdaterRef = Boolean(
    updater?.hasReferenceId ||
    (updater?.id && updater.id !== "N/A" && typeof candidate?.updatedBy !== "string"),
  );
  const updaterName = getDisplayValue(
    updater?.name ||
    (typeof candidate?.updatedBy === "string" ? candidate?.updatedBy : ""),
    null,
  );
  const updaterDesignation = hasUpdaterRef
    ? getDisplayValue(updater?.designation, "")
    : null;
  const updaterEmployeeId = hasUpdaterRef
    ? getDisplayValue(
      updater?.employeeId && !isMongoObjectId(updater.employeeId)
        ? updater.employeeId
        : "",
      null,
    )
    : null;
  const rawUpdatedAt = candidate?.updatedAt;
  const updatedAtFormatted = rawUpdatedAt
    ? formatDate(rawUpdatedAt, { includeTime: true })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4 sm:flex-grow min-w-0">
              <Button
                onClick={() => (onBack ? onBack() : (location.state?.from ? navigate(location.state.from) : navigate(-1)))}
                variant="outline"
                className="flex items-center gap-2 w-fit"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 capitalize break-words">
                  {fullName}
                </h1>
                <p className="text-sm text-gray-600 capitalize break-words">
                  {jobTitle}
                </p>
                <div className="text-gray-600 break-words flex flex-wrap items-center gap-3">
                  <span>Candidate ID: {candidateId}</span>
                  {candidate.appliedDate && (
                    <span className="text-sm flex items-center gap-1 text-gray-500">
                      <Clock className="h-4 w-4 flex-shrink-0" />
                      Applied on{" "}
                      {formatDate(candidate.appliedDate, {
                        includeTime: false,
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium w-fit ${getStatusColor(candidate.status)}`}
              >
                {getStatusIcon(candidate.status)}
                <span className="ml-1 capitalize">{candidate.status}</span>
              </span>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button
                  variant="outline"
                  icon={<Edit className="h-4 w-4" />}
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button
                  variant="primary"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleDownloadResume}
                  loading={isDownloading}
                >
                  Download Resume
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiMiniCard
            icon={Award}
            title="Expected Salary"
            value={
              candidate.expectedSalary &&
                candidate.expectedSalary !== "Not specified"
                ? typeof candidate.expectedSalary === "number"
                  ? `₹${candidate.expectedSalary.toLocaleString()}`
                  : candidate.expectedSalary
                : "Not specified"
            }
          />
          <KpiMiniCard icon={Briefcase} title="Experience Level" value={experience || "Not specified"} />
          <KpiMiniCard icon={Globe} title="Source" value={source || "Not specified"} />
          <KpiMiniCard icon={Clock} title="Availability" value={availability || "Not specified"} />
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Main Content */}
          <div className="space-y-4 sm:space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                    {fullName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2 break-words">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="break-words">{candidate.email}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {candidate.phone}
                  </p>
                </div>
                {/* Location Information */}
                <div className="col-span-1 md:col-span-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location Information
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {candidate?.address?.streetAddress && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wide">
                          Street Address
                        </label>
                        <p className="mt-1 text-sm text-gray-900 break-words">
                          {candidate.address.streetAddress}
                        </p>
                      </div>
                    )}
                    {candidate?.address?.postalCode && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wide">
                          Postal Code
                        </label>
                        <p className="mt-1 text-sm text-gray-900 break-words">
                          {candidate.address.postalCode}
                        </p>
                      </div>
                    )}
                    {candidate?.address?.city?.name && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wide">
                          City
                        </label>
                        <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                          {candidate.address.city.name}
                        </p>
                      </div>
                    )}
                    {candidate?.address?.state?.name && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wide">
                          State
                        </label>
                        <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                          {candidate.address.state.name}
                        </p>
                      </div>
                    )}
                    {candidate?.address?.country?.name && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wide">
                          Country
                        </label>
                        <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                          {candidate.address.country.name}
                          {candidate.address.country.iso2 &&
                            ` (${candidate.address.country.iso2})`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Job Information
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job Title
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                    {jobTitle}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                    {jobDepartment}
                  </p>
                </div>
              </div>
            </div>

            {/* Salary Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="h-5 w-5" />
                Salary Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Expected Salary
                  </label>
                  <p className="mt-1 text-base font-semibold text-gray-900 break-words">
                    {candidate.expectedSalary &&
                      candidate.expectedSalary !== "Not specified"
                      ? typeof candidate.expectedSalary === "number"
                        ? `₹${candidate.expectedSalary.toLocaleString()}`
                        : candidate.expectedSalary
                      : "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notice Period
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {candidate.noticePeriod &&
                      candidate.noticePeriod !== "Not specified"
                      ? `${candidate.noticePeriod}`
                      : "Not specified"}
                  </p>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="h-5 w-5" />
                Professional Information
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Experience Level
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {experience || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Current Company
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                    {currentCompany}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Source
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                    {source}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Availability
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                    {availability}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Website Region
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {(() => {
                      const regionOptions = [
                        { value: "MOBILOITTE", label: "Mobiloitte" },
                        { value: "MOBILOITTE_UAE", label: "Mobiloitte UAE" },
                        { value: "MOBILOITTE_USA", label: "Mobiloitte USA" },
                        { value: "MOBILOITTE_UK", label: "Mobiloitte UK" },
                        { value: "MOBILOITTE_SOUTH_AFRICA", label: "Mobiloitte South Africa" },
                        { value: "MOBILOITTE_SINGAPORE", label: "Mobiloitte Singapore" },
                        { value: "MOBILOITTE_GROUP", label: "Mobiloitte Group" }
                      ];
                      const region = regionOptions.find(r => r.value === candidate.websiteRegion);
                      return region ? region.label : (candidate.websiteRegion || "Not specified");
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Education Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Education Information
              </h2>
              {candidate.educations && candidate.educations.length > 0 ? (
                <div className="space-y-6">
                  {candidate.educations.map((edu, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Education
                          </label>
                          <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                            {edu.education || "Not specified"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            University
                          </label>
                          <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                            {edu.university || "Not specified"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Graduation Year
                          </label>
                          <p className="mt-1 text-sm text-gray-900 break-words">
                            {edu.graduationYear || "Not specified"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Education
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                      {education || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      University
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                      {university || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Graduation Year
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words">
                      {candidate.graduationYear || "Not specified"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Social Links
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    LinkedIn
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {candidate.linkedin ? (
                      <a
                        href={candidate.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand hover:text-blue-800 flex items-center gap-1"
                      >
                        <Linkedin className="h-4 w-4" />
                        View Profile
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    GitHub
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {candidate.github ? (
                      <a
                        href={candidate.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand hover:text-blue-800 flex items-center gap-1"
                      >
                        <Github className="h-4 w-4" />
                        View Profile
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Portfolio
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {candidate.portfolio ? (
                      <a
                        href={candidate.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand hover:text-blue-800 flex items-center gap-1"
                      >
                        <Globe className="h-4 w-4" />
                        View Portfolio
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {candidate.notes && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Additional Notes
                </h2>
                <div>
                  <p
                    className="text-sm text-gray-900 whitespace-pre-wrap break-words"
                    style={{
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {candidate.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Skills */}
            {candidate.skills && candidate.skills.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Skills
                </h2>
                <div className="flex flex-wrap gap-2">
                  {candidate.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-blue-50/30 text-brand border border-blue-100 break-words max-w-full whitespace-normal capitalize"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {candidate.notes && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes
                </h2>
                <p
                  className="text-sm text-gray-700 whitespace-pre-wrap break-words"
                  style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {candidate.notes}
                </p>
              </div>
            )}
          </div>

          {/* Stage Movement History */}
          {candidate.stageMovementHistory &&
            candidate.stageMovementHistory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mt-6">
                <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Stage Movement History
                </h2>
                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent">
                  {candidate.stageMovementHistory.map((history, index) => (
                    <div
                      key={index}
                      className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
                    >
                      {/* Icon */}
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-200 text-slate-500 group-[.is-active]:bg-brand group-[.is-active]:text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                        <Clock className="h-4 w-4" />
                      </div>
                      {/* Content */}
                      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded border border-slate-200 bg-white shadow-sm">
                        <div className="flex items-center justify-between space-x-2 mb-1">
                          <div className="font-bold text-slate-900 capitalize flex items-center gap-2">
                            {history.fromStage} &rarr; {history.toStage}
                            {(history.toStage === "interview" || history.toStage === "interviewed") && history.referenceId && (
                              <button
                                onClick={() => navigate(`/view_interview/${history.referenceId}`)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-brand/10 text-brand rounded-md hover:bg-brand/20 transition-colors cursor-pointer"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View Interview
                              </button>
                            )}
                          </div>
                          <time className="text-xs font-medium text-slate-500 shrink-0">
                            {formatDate(history.movedAt, { includeTime: true })}
                          </time>
                        </div>
                        <div className="text-sm text-slate-600 mb-2 first-letter:capitalize break-words break-words">
                          {history.reason}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>Moved by: </span>
                          <span className="font-medium text-slate-700">
                            {history.movedBy
                              ? history.movedBy.fullName ||
                              history.movedBy.name ||
                              [
                                history.movedBy.firstName,
                                history.movedBy.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ") ||
                              "System"
                              : "System"}
                          </span>
                          {history.movedBy && (history.movedBy.employeeId || history.movedBy.department) && (
                            <span className="text-slate-400">
                              ({[history.movedBy.employeeId, history.movedBy.department].filter(Boolean).join(" - ")})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Candidate Creation & Update Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-brand" />
              Candidate Creation & Update Details
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Created By
                </label>
                <p className="mt-1 text-sm font-medium text-gray-900 capitalize flex items-center gap-1.5 break-words">
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{creatorName}</span>
                </p>
              </div>

              {/* {hasCreatorRef && creatorDesignation && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Role
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 capitalize flex items-center gap-1.5 break-words">
                    <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{creatorDesignation}</span>
                  </p>
                </div>
              )} */}

              {hasCreatorRef && creatorEmployeeId && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Employee ID
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 flex items-center gap-1.5 break-words">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{creatorEmployeeId}</span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Created At
                </label>
                <p className="mt-1 text-sm font-medium text-gray-900 flex items-center gap-1.5 break-words">
                  <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{createdAtFormatted || "N/A"}</span>
                </p>
              </div>

              {updaterName && updaterName !== "N/A" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Last Updated By
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 capitalize flex items-center gap-1.5 break-words">
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{updaterName}</span>
                  </p>
                </div>
              )}

              {/* {hasUpdaterRef && updaterDesignation && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Role
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 capitalize flex items-center gap-1.5 break-words">
                    <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{updaterDesignation}</span>
                  </p>
                </div>
              )} */}

              {hasUpdaterRef && updaterEmployeeId && updaterEmployeeId !== "N/A" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Employee ID
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 flex items-center gap-1.5 break-words">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{updaterEmployeeId}</span>
                  </p>
                </div>
              )}

              {updatedAtFormatted && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Last Updated At
                  </label>
                  <p className="mt-1 text-sm font-medium text-gray-900 flex items-center gap-1.5 break-words">
                    <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span>{updatedAtFormatted}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Delete Candidate
                    </h3>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-gray-900">
                      {candidate?.firstName} {candidate?.lastName}
                    </span>
                    ?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone. All candidate data, including
                    resume and application history, will be permanently deleted.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Candidate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Custom Alert */}
        <CustomAlert
          isOpen={alertState.show}
          onClose={hideAlert}
          title={alertState.title}
          message={alertState.message}
          type={alertState.type}
          showCancel={alertState.showCancel}
          onConfirm={alertState.onConfirm}
          confirmText={alertState.confirmText}
          cancelText={alertState.cancelText}
        />
      </div>
    </div>
  );
};

export default ViewCandidate;
